// NEXUS LUXE PRO - LIGHTWEIGHT MOTION ENGINE
// Enterprise-grade animation system for cinematic experiences
// v1.0 - Production Ready

class NexusMotionEngine {
  constructor() {
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.rafCallbacks = new Set();
    this.scrollCallbacks = new Set();
    this.intersectionObservers = new Map();
    this.currentScrollY = 0;
    this.rafId = null;

    this.init();
  }

  init() {
    if (this.isReducedMotion) {
      document.documentElement.classList.add('reduce-motion');
      return;
    }

    this.setupAccessibility();
    this.setupRAFLoop();
    this.setupScrollHandler();
  }

  // Core RAF Loop
  setupRAFLoop() {
    const loop = () => {
      this.rafCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.warn('Motion callback error:', error);
        }
      });

      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);

    // Cleanup on unload
    window.addEventListener('beforeunload', () => {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    });
  }

  // Scroll Handler with Throttling
  setupScrollHandler() {
    let ticking = false;

    const handleScroll = () => {
      this.currentScrollY = window.scrollY;

      if (!ticking) {
        this.rafId = requestAnimationFrame(() => {
          this.scrollCallbacks.forEach(callback => {
            try {
              callback(this.currentScrollY);
            } catch (error) {
              console.warn('Scroll callback error:', error);
            }
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  // Accessibility Handling
  setupAccessibility() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    mediaQuery.addEventListener('change', (e) => {
      if (e.matches) {
        document.documentElement.classList.add('reduce-motion');
        this.disableAllMotion();
      } else {
        document.documentElement.classList.remove('reduce-motion');
        this.enableAllMotion();
      }
    });
  }

  // Register RAF Callback
  onFrame(callback) {
    if (typeof callback === 'function') {
      this.rafCallbacks.add(callback);
    }
    return () => this.rafCallbacks.delete(callback);
  }

  // Register Scroll Callback
  onScroll(callback) {
    if (typeof callback === 'function') {
      this.scrollCallbacks.add(callback);
    }
    return () => this.scrollCallbacks.delete(callback);
  }

  // Intersection Observer Factory
  createObserver(options = {}) {
    const defaultOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const callback = this.intersectionObservers.get(entry.target);
        if (callback) {
          callback(entry);
        }
      });
    }, { ...defaultOptions, ...options });

    return {
      observe: (element, callback) => {
        this.intersectionObservers.set(element, callback);
        observer.observe(element);
      },
      unobserve: (element) => {
        this.intersectionObservers.delete(element);
        observer.unobserve(element);
      },
      disconnect: () => observer.disconnect()
    };
  }

  // Smooth Value Interpolation
  interpolate(start, end, progress) {
    return start + (end - start) * progress;
  }

  // Easing Functions
  easings = {
    linear: t => t,
    easeInQuad: t => t * t,
    easeOutQuad: t => t * (2 - t),
    easeInOutQuad: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    easeInCubic: t => t * t * t,
    easeOutCubic: t => (--t) * t * t + 1,
    easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
    easeInQuart: t => t * t * t * t,
    easeOutQuart: t => 1 - (--t) * t * t * t,
    easeInOutQuart: t => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t,
    premium: t => {
      // Custom cubic-bezier(0.22, 1, 0.36, 1)
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
  };

  // Scroll Progress Calculation
  getScrollProgress(element, offsetStart = 0, offsetEnd = 0) {
    const rect = element.getBoundingClientRect();
    const elementTop = rect.top + window.scrollY;
    const elementBottom = elementTop + rect.height;

    const scrollStart = elementTop - window.innerHeight + offsetStart;
    const scrollEnd = elementBottom + offsetEnd;

    if (this.currentScrollY < scrollStart) return 0;
    if (this.currentScrollY > scrollEnd) return 1;

    return (this.currentScrollY - scrollStart) / (scrollEnd - scrollStart);
  }

  // Viewport Check
  isInViewport(element, threshold = 0) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= -threshold &&
      rect.left >= -threshold &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + threshold &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth) + threshold
    );
  }

  // Performance Utilities
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Disable/Enable All Motion
  disableAllMotion() {
    this.rafCallbacks.clear();
    this.scrollCallbacks.clear();
    this.intersectionObservers.clear();
  }

  enableAllMotion() {
    // Re-initialize as needed
    this.setupRAFLoop();
    this.setupScrollHandler();
  }

  // Cleanup Method
  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafCallbacks.clear();
    this.scrollCallbacks.clear();
    this.intersectionObservers.clear();
  }
}

// Initialize Global Instance
document.addEventListener('DOMContentLoaded', () => {
  window.NexusMotionEngine = new NexusMotionEngine();
});

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NexusMotionEngine;
}